import { useMemo, useState } from 'react'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, Link, notFound } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { ArrowLeftIcon, PlusIcon, Trash2Icon } from 'lucide-react'

import { Alert, AlertDescription, AlertTitle } from '@ems/ui/components/alert'
import { Button } from '@ems/ui/components/button'
import { Input } from '@ems/ui/components/input'
import { DataTable } from '@ems/ui/patterns/data-table'
import { Field } from '@ems/ui/patterns/field'
import { PageHeader } from '@ems/ui/patterns/page-header'
import { StatusBadge } from '@ems/ui/patterns/status-badge'

import { OptionSelect } from '#admin/components/option-select.tsx'
import { NotFoundView } from '#admin/components/not-found-view.tsx'
import { sessionQuery } from '#admin/features/auth/queries.ts'
import type { Session } from '#admin/features/auth/session.ts'
import { hasPlatformScope, isPlatformStaff } from '#admin/features/platform/api.ts'
import { tenantsQuery } from '#admin/features/platform/tenants/queries.ts'
import { actionLabel, type UserDetail } from '#admin/features/platform/users/api.ts'
import { useStaffActions, useStartImpersonation } from '#admin/features/platform/users/mutations.ts'
import { userQuery } from '#admin/features/platform/users/queries.ts'
import { ReasonDialog } from '#admin/features/platform/reason-dialog.tsx'
import { AddMembershipDialog } from '#admin/features/platform/users/add-membership-dialog.tsx'
import { TenantRoleSelect } from '#admin/features/platform/users/tenant-role-select.tsx'
import { UserSkeleton } from '#admin/features/platform/platform-skeletons.tsx'

/**
 * `/platform/users/$userId` — one login, and everything staff may do to it.
 *
 * **Each button is offered only to staff holding the power it needs** (`hasPlatformScope`), and
 * only on a login it can apply to — nobody is offered "impersonate" on another member of staff.
 * The server enforces every one of those rules again; this keeps the page from offering what it
 * would refuse.
 *
 * **Every act asks for a reason**, recorded with the actor in the history at the foot of the page.
 */
export const Route = createFileRoute('/_authenticated/platform/users/$userId')({
  pendingComponent: UserSkeleton,
  staticData: { crumb: useUserCrumb },
  loader: async ({ context, params }) => {
    const session = await context.queryClient.ensureQueryData(sessionQuery())
    if (!isPlatformStaff(session)) throw notFound()
    await Promise.all([
      context.queryClient.ensureQueryData(userQuery(params.userId)),
      context.queryClient.ensureQueryData(tenantsQuery()),
    ])
  },
  // Its own, so the refusal renders in the layout's outlet with the sidebar intact. A thrown
  // not-found goes to the nearest route declaring a component for it, and that route renders it in
  // place of itself — so the layout must not be the one to catch it.
  notFoundComponent: NotFoundView,
  component: UserPage,
})

function useUserCrumb(): string {
  const { userId } = Route.useParams()
  return useQuery(userQuery(userId)).data?.email ?? 'User'
}

type Dialog =
  | { kind: 'impersonate' }
  | { kind: 'ban' }
  | { kind: 'unban' }
  | { kind: 'revoke' }
  | { kind: 'membership'; tenantId: string; tenantName: string; role: string }
  | { kind: 'leave'; tenantId: string; tenantName: string }
  | { kind: 'add' }
  | null

const when = (iso: string) => new Date(iso).toLocaleString()

function UserPage() {
  const { userId } = Route.useParams()
  const { data: user } = useSuspenseQuery(userQuery(userId))
  const { data: session } = useSuspenseQuery(sessionQuery())
  const [dialog, setDialog] = useState<Dialog>(null)
  const { data: tenants } = useSuspenseQuery(tenantsQuery())

  const actions = useStaffActions(userId)
  const impersonate = useStartImpersonation()

  const isStaff = user.staffScopes !== null
  const isSelf = session.user?.id === user.id
  const manage = hasPlatformScope(session, 'platform:users.manage') && !isStaff && !isSelf
  const canImpersonate =
    hasPlatformScope(session, 'platform:impersonate') && !isStaff && !isSelf && !user.banned

  const close = () => setDialog(null)

  return (
    <div className="flex flex-col gap-8">
      <div>
        <Link
          to="/platform/users"
          search={{ q: '' }}
          className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-sm"
        >
          <ArrowLeftIcon className="size-4" />
          Users
        </Link>
        <PageHeader
          className="mt-1"
          title={user.email}
          description={`${user.name} · created ${new Date(user.createdAt).toLocaleDateString()}`}
        />
        <div className="mt-3 flex flex-wrap gap-1">
          {user.banned ? <StatusBadge tone="danger">banned</StatusBadge> : null}
          {isStaff ? <StatusBadge tone="info">platform staff</StatusBadge> : null}
          <StatusBadge tone={user.emailVerified ? 'success' : 'neutral'}>
            {user.emailVerified ? 'email verified' : 'email not verified'}
          </StatusBadge>
          <StatusBadge tone={user.twoFactorEnabled ? 'success' : 'neutral'}>
            {user.twoFactorEnabled ? 'two-factor on' : 'two-factor off'}
          </StatusBadge>
          <StatusBadge tone="neutral">
            {user.signInMethods.length === 0
              ? 'magic link only'
              : `signs in with ${user.signInMethods
                  // Better Auth's provider id for a password.
                  .map((method) => (method === 'credential' ? 'password' : method))
                  .join(', ')}`}
          </StatusBadge>
        </div>
      </div>

      {user.banned ? (
        <Alert variant="destructive">
          <AlertTitle>
            Banned{user.banExpires === null ? '' : ` until ${when(user.banExpires)}`}
          </AlertTitle>
          <AlertDescription>{user.banReason ?? 'No reason recorded.'}</AlertDescription>
        </Alert>
      ) : null}

      <ActionsCard
        user={user}
        session={session}
        isStaff={isStaff}
        isSelf={isSelf}
        manage={manage}
        canImpersonate={canImpersonate}
        signInLinkPending={actions.signInLink.isPending}
        onSignInLink={() => actions.signInLink.mutate()}
        open={setDialog}
      />

      <MembershipsSection user={user} manage={manage} open={setDialog} />
      <SessionsSection user={user} />
      <HistorySection user={user} />

      {/* ---- Dialogs. One at a time, each recording its reason. ---- */}

      <ImpersonateDialog
        open={dialog?.kind === 'impersonate'}
        onOpenChange={(open) => (open ? undefined : close())}
        user={user}
        busy={impersonate.isPending}
        onConfirm={(reason, tenantId) =>
          impersonate.mutate({
            userId,
            reason,
            ...(tenantId === '' ? {} : { tenantId }),
          })
        }
      />

      <BanDialog
        open={dialog?.kind === 'ban'}
        onOpenChange={(open) => (open ? undefined : close())}
        email={user.email}
        busy={actions.ban.isPending}
        onConfirm={(reason, expiresAt) =>
          actions.ban.mutate(
            { reason, ...(expiresAt === undefined ? {} : { expiresAt }) },
            { onSuccess: close },
          )
        }
      />

      <ReasonDialog
        open={dialog?.kind === 'unban'}
        onOpenChange={(open) => (open ? undefined : close())}
        title={`Lift the ban on ${user.email}?`}
        description="They can sign in again straight away."
        confirmLabel="Lift ban"
        busy={actions.unban.isPending}
        onConfirm={(reason) => actions.unban.mutate(reason, { onSuccess: close })}
      />

      <ReasonDialog
        open={dialog?.kind === 'revoke'}
        onOpenChange={(open) => (open ? undefined : close())}
        title={`Sign ${user.email} out everywhere?`}
        description="Every session this login has ends now, on every device. They can sign straight back in."
        confirmLabel="Sign out everywhere"
        destructive
        busy={actions.revoke.isPending}
        onConfirm={(reason) => actions.revoke.mutate(reason, { onSuccess: close })}
      />

      <ReasonDialog
        open={dialog?.kind === 'membership'}
        onOpenChange={(open) => (open ? undefined : close())}
        title={
          dialog?.kind === 'membership'
            ? `Make ${user.email} ${dialog.role} in ${dialog.tenantName}?`
            : ''
        }
        description="Recorded here and in that edition's own audit log, so its operators can see who changed it."
        confirmLabel="Save membership"
        busy={actions.membership.isPending}
        onConfirm={(reason) => {
          if (dialog?.kind !== 'membership') return
          actions.membership.mutate(
            { tenantId: dialog.tenantId, role: dialog.role, reason },
            { onSuccess: close },
          )
        }}
      />

      <AddMembershipDialog
        open={dialog?.kind === 'add'}
        onOpenChange={(open) => (open ? undefined : close())}
        email={user.email}
        editions={tenants.items.filter(
          (tenant) => !user.memberships.some((m) => m.tenantId === tenant.id),
        )}
        busy={actions.membership.isPending}
        onConfirm={(input) => actions.membership.mutate(input, { onSuccess: close })}
      />

      <ReasonDialog
        open={dialog?.kind === 'leave'}
        onOpenChange={(open) => (open ? undefined : close())}
        title={dialog?.kind === 'leave' ? `Remove ${user.email} from ${dialog.tenantName}?` : ''}
        description="They lose access to that edition on their next request. Their login and other editions are untouched."
        confirmLabel="Remove"
        destructive
        busy={actions.leave.isPending}
        onConfirm={(reason) => {
          if (dialog?.kind !== 'leave') return
          actions.leave.mutate({ tenantId: dialog.tenantId, reason }, { onSuccess: close })
        }}
      />
    </div>
  )
}

function ActionsCard({
  user,
  session,
  isStaff,
  isSelf,
  manage,
  canImpersonate,
  signInLinkPending,
  onSignInLink,
  open,
}: {
  user: UserDetail
  session: Session
  isStaff: boolean
  isSelf: boolean
  manage: boolean
  canImpersonate: boolean
  signInLinkPending: boolean
  onSignInLink: () => void
  open: (dialog: Dialog) => void
}) {
  if (isStaff || isSelf) {
    return (
      <p className="text-muted-foreground rounded-lg border border-(--border) p-4 text-sm">
        {isSelf
          ? 'This is your own login. Staff acts cannot be taken on yourself.'
          : 'Platform staff are managed with scripts/platform-staff.ts, not from the console — so offboarding a member of staff is visible in a deployment record.'}
      </p>
    )
  }

  const nothing = !manage && !canImpersonate
  return (
    <section className="bg-card flex flex-col gap-3 rounded-lg border border-(--border) p-4 md:p-6">
      <h2 className="text-base font-semibold">Actions</h2>
      {nothing ? (
        <p className="text-muted-foreground text-sm">
          Your staff role can see this login but not act on it.
          {hasPlatformScope(session, 'platform:impersonate') && user.banned
            ? ' A banned login cannot be impersonated.'
            : ''}
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {canImpersonate ? (
            <Button onClick={() => open({ kind: 'impersonate' })}>Impersonate</Button>
          ) : null}
          {manage && !user.banned ? (
            <Button variant="outline" disabled={signInLinkPending} onClick={onSignInLink}>
              Send sign-in link
            </Button>
          ) : null}
          {manage ? (
            <Button variant="outline" onClick={() => open({ kind: 'revoke' })}>
              Sign out everywhere
            </Button>
          ) : null}
          {manage && !user.banned ? (
            <Button variant="destructive" onClick={() => open({ kind: 'ban' })}>
              Ban
            </Button>
          ) : null}
          {manage && user.banned ? (
            <Button variant="outline" onClick={() => open({ kind: 'unban' })}>
              Lift ban
            </Button>
          ) : null}
        </div>
      )}
    </section>
  )
}

function ImpersonateDialog({
  open,
  onOpenChange,
  user,
  busy,
  onConfirm,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  user: UserDetail
  busy: boolean
  onConfirm: (reason: string, tenantId: string) => void
}) {
  // `first` rather than an empty string: Radix reserves `""` for "nothing chosen".
  const [tenantId, setTenantId] = useState('first')
  return (
    <ReasonDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Sign in as ${user.email}?`}
      description="You will see the console exactly as they do, for thirty minutes at most. Anything you change is recorded as done by them on your behalf, and their password, email, two-factor and access cannot be changed while you are them."
      confirmLabel="Start impersonating"
      busy={busy}
      onConfirm={(reason) => onConfirm(reason, tenantId === 'first' ? '' : tenantId)}
    >
      {user.memberships.length > 1 ? (
        <Field label="Edition" help="Where to land. They can have different roles in each.">
          {(props) => (
            <OptionSelect
              {...props}
              value={tenantId}
              onValueChange={setTenantId}
              options={[
                { value: 'first', label: 'Their first edition' },
                ...user.memberships.map((m) => ({
                  value: m.tenantId,
                  label: `${m.tenantName} (${m.role})`,
                })),
              ]}
            />
          )}
        </Field>
      ) : null}
    </ReasonDialog>
  )
}

function BanDialog({
  open,
  onOpenChange,
  email,
  busy,
  onConfirm,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  email: string
  busy: boolean
  onConfirm: (reason: string, expiresAt: string | undefined) => void
}) {
  const [until, setUntil] = useState('')
  return (
    <ReasonDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Ban ${email}?`}
      description="They are signed out everywhere now and cannot sign in to any edition until the ban ends or is lifted."
      confirmLabel="Ban"
      destructive
      busy={busy}
      onConfirm={(reason) =>
        onConfirm(reason, until === '' ? undefined : new Date(until).toISOString())
      }
    >
      <Field label="Until" help="Leave empty for a ban with no end.">
        {(props) => (
          <Input
            {...props}
            type="datetime-local"
            value={until}
            onChange={(event) => setUntil(event.target.value)}
          />
        )}
      </Field>
    </ReasonDialog>
  )
}

type Membership = UserDetail['memberships'][number]

function MembershipsSection({
  user,
  manage,
  open,
}: {
  user: UserDetail
  manage: boolean
  open: (dialog: Dialog) => void
}) {
  const { data: tenants } = useSuspenseQuery(tenantsQuery())
  const canAdd = tenants.items.some((t) => !user.memberships.some((m) => m.tenantId === t.id))
  const columns = useMemo<ColumnDef<Membership, never>[]>(
    () => [
      {
        id: 'edition',
        header: 'Edition',
        meta: { primary: true },
        cell: ({ row }) => (
          <span className="flex flex-col">
            <span className="font-medium">{row.original.tenantName}</span>
            <span className="text-muted-foreground font-mono text-xs">
              {row.original.tenantSlug}
            </span>
          </span>
        ),
      },
      {
        id: 'role',
        header: 'Role',
        cell: ({ row }) =>
          manage ? (
            <TenantRoleSelect
              tenantId={row.original.tenantId}
              value={row.original.role}
              label={`Role in ${row.original.tenantName}`}
              onChange={(role) =>
                open({
                  kind: 'membership',
                  tenantId: row.original.tenantId,
                  tenantName: row.original.tenantName,
                  role,
                })
              }
            />
          ) : (
            row.original.role
          ),
      },
      {
        id: 'joined',
        header: 'Joined',
        cell: ({ row }) => (
          <span className="text-muted-foreground">
            {new Date(row.original.joinedAt).toLocaleDateString()}
          </span>
        ),
      },
      {
        id: 'actions',
        header: () => <span className="sr-only">Actions</span>,
        meta: { align: 'end' },
        cell: ({ row }) =>
          manage ? (
            <Button
              variant="ghost"
              size="sm"
              // Destructive in tone, not a solid red block: one per row would shout louder than
              // Ban, and the confirmation that follows is where the solid red belongs.
              className="text-destructive hover:bg-destructive/10 hover:text-destructive dark:hover:bg-destructive/20"
              aria-label={`Remove from ${row.original.tenantName}`}
              onClick={() =>
                open({
                  kind: 'leave',
                  tenantId: row.original.tenantId,
                  tenantName: row.original.tenantName,
                })
              }
            >
              <Trash2Icon />
              Remove
            </Button>
          ) : null,
      },
    ],
    [manage, open],
  )

  return (
    <DataTable
      caption="Editions this login belongs to"
      title="Editions"
      description="Memberships and roles. A login with none signs in and sees nothing."
      actions={
        manage && canAdd ? (
          <Button variant="outline" size="sm" onClick={() => open({ kind: 'add' })}>
            <PlusIcon />
            Add to edition
          </Button>
        ) : null
      }
      columns={columns}
      data={user.memberships}
      getRowId={(m) => m.tenantId}
      empty={<p className="text-muted-foreground p-6 text-sm">Not a member of any edition.</p>}
    />
  )
}

/** A role picker for one edition, filled from that edition's own roles. */
type LiveSession = UserDetail['sessions'][number]

function SessionsSection({ user }: { user: UserDetail }) {
  const columns = useMemo<ColumnDef<LiveSession, never>[]>(
    () => [
      {
        id: 'started',
        header: 'Started',
        meta: { primary: true },
        cell: ({ row }) => when(row.original.createdAt),
      },
      { id: 'expires', header: 'Expires', cell: ({ row }) => when(row.original.expiresAt) },
      {
        id: 'where',
        header: 'From',
        cell: ({ row }) => (
          <span className="text-muted-foreground text-xs">
            {row.original.ipAddress === null || row.original.ipAddress === ''
              ? 'unknown address'
              : row.original.ipAddress}
            {row.original.userAgent === null ? '' : ` · ${row.original.userAgent.slice(0, 80)}`}
          </span>
        ),
      },
      {
        id: 'kind',
        header: 'Kind',
        cell: ({ row }) =>
          row.original.impersonatedBy === null ? (
            <StatusBadge tone="neutral">their own</StatusBadge>
          ) : (
            <StatusBadge tone="warning">staff impersonating</StatusBadge>
          ),
      },
    ],
    [],
  )

  return (
    <DataTable
      caption="Live sessions"
      title="Sessions"
      description="Signed in right now. Tokens are never shown."
      columns={columns}
      data={user.sessions}
      getRowId={(s) => s.id}
      empty={<p className="text-muted-foreground p-6 text-sm">No live sessions.</p>}
    />
  )
}

function HistorySection({ user }: { user: UserDetail }) {
  return (
    <section className="bg-card flex flex-col gap-3 rounded-lg border border-(--border) p-4 md:p-6">
      <h2 className="text-base font-semibold">What staff have done</h2>
      {user.history.length === 0 ? (
        <p className="text-muted-foreground text-sm">Nothing yet.</p>
      ) : (
        <ol className="flex flex-col gap-3">
          {user.history.map((entry) => (
            <li key={entry.id} className="border-l-2 border-(--border) pl-3">
              <p className="text-sm">
                <span className="font-medium">{actionLabel(entry.action)}</span>
                <span className="text-muted-foreground">
                  {' '}
                  by {entry.actorEmail ?? 'a removed account'} · {when(entry.createdAt)}
                </span>
              </p>
              {entry.reason === null ? null : (
                <p className="text-muted-foreground text-sm">“{entry.reason}”</p>
              )}
            </li>
          ))}
        </ol>
      )}
    </section>
  )
}
